import { Button, Stack, Typography } from '@mui/material';
import { DrawerHeader } from '../../../../../drawer/DrawerHeader';
import { FC, useState } from 'react';
import { z } from 'zod';
import { DataPointTable } from '../../../../../connections/dataPoints/DataPointTable';
import { graphql } from '../../../../../gql';
import { useApiMutation, useApiQuery } from '../../../../../query/hooks';
import { Switcher } from '../../../../../layout/Switcher';
import { FormattedMessage, useIntl } from 'react-intl';
import { Add, DeleteOutline, EditOutlined, MoreHoriz } from '@mui/icons-material';
import { useModalContext } from '../../../../../modal/ModalContext';
import { CustomPopover } from '../../../../../ui/CustomPopover';
import { mutationResponseHandler } from '../../../../../utils/mutationResponseHandler';
import { useDrawerContext } from '../../../../../drawer/DrawerContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import qs from 'qs';
import { SourceDrawerTab } from './SourceDrawerTab';
import { useTypedQueryParams } from '../../../../../hooks/useTypedQueryParams';
import { AlertsDrawerTab } from './AlertsDrawerTab';
import { DrawerWrapper } from '../../../../../drawer/DrawerWrapper';
import { useSnackbar } from '../../../../../store/snackbar';

const getMetricQuery = graphql(`
	query editMetricWidgetDrawer_getMetric($contentMetricId: UUID!) {
		getContentMetric(by: { id: $contentMetricId }) {
			metric {
				name
			}
		}
	}
`);

const updateActivityMutation = graphql(`
	mutation editMetricWidgetDrawer_updateActivity($activityId: UUID!, $data: ActivityUpdateInput!) {
		updateActivity(by: { id: $activityId }, data: $data) {
			ok
			errorMessage
		}
	}
`);

export const editMetricWidgetDrawerSchema = z.object({
	activityId: z.string(),
	contentMetricId: z.string(),
});

const tabSchema = z.object({
	tab: z.enum(['HISTORY', 'ALERTS', 'SOURCE']).optional(),
});

type Tabs = z.infer<typeof tabSchema>['tab'];

export const EditMetricWidgetDrawer: FC<z.infer<typeof editMetricWidgetDrawerSchema>> = ({ activityId, contentMetricId }) => {
	const intl = useIntl();
	const navigate = useNavigate();
	const { closeDrawer } = useDrawerContext();
	const { setModal } = useModalContext();
	const { data } = useApiQuery(getMetricQuery, {
		variables: { contentMetricId },
	});
	const [searchParams, setSearchParams] = useSearchParams();
	const [{ tab }] = useTypedQueryParams(tabSchema);
	const [tabState, setTabState] = useState<Tabs>((tab as Tabs) ?? 'HISTORY');
	const { openSnackbar } = useSnackbar();

	const { mutate: updateActivity } = useApiMutation(updateActivityMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.updateActivity.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'metrics-widget.updated' }),
					error: intl.formatMessage({ id: 'common.error.default' }),
				},
				errorMessage: result.updateActivity.errorMessage,
				openSnackbar,
			});
		},
	});

	const handleRemoveMetric = () => {
		updateActivity({
			activityId,
			data: {
				contentMetrics: [
					{
						disconnect: {
							id: contentMetricId,
						},
					},
				],
				touchpoint: {
					update: {
						phase: {
							update: {
								contentMetrics: [
									{
										disconnect: {
											id: contentMetricId,
										},
									},
								],
							},
						},
					},
				},
			},
		});
		closeDrawer();
	};

	const handleEditMetric = () => {
		navigate(`/connections/metrics?drawer%5Btype%5D=editMetric&drawer%5BcontentMetricId%5D=${contentMetricId}`);
	};

	const handleAddData = () => {
		setModal({ type: 'editMetricDataPoint', payload: { contentMetricId }, maxWidth: 'xs' });
	};

	const tabsConfig = [
		{
			item: 'HISTORY',
			title: <FormattedMessage id={'metrics-widget.switcher.history'} />,
			onNavigate: () => {
				setTabState('HISTORY');
				setSearchParams(() => {
					const { ...rest } = qs.parse(searchParams.toString());
					return qs.stringify({ ...rest, tab: 'HISTORY' });
				});
			},
			isActive: () => tabState === 'HISTORY',
		},
		{
			item: 'ALERTS',
			title: <FormattedMessage id={'metrics-widget.switcher.alerts'} />,
			onNavigate: () => {
				setTabState('ALERTS');
				setSearchParams(() => {
					const { ...rest } = qs.parse(searchParams.toString());
					return qs.stringify({ ...rest, tab: 'ALERTS' });
				});
			},
			isActive: () => tabState === 'ALERTS',
		},
		{
			item: 'SOURCE',
			title: <FormattedMessage id={'metrics-widget.switcher.source'} />,
			onNavigate: () => {
				setTabState('SOURCE');
				setSearchParams(() => {
					const { ...rest } = qs.parse(searchParams.toString());
					return qs.stringify({ ...rest, tab: 'SOURCE' });
				});
			},
			isActive: () => tabState === 'SOURCE',
		},
	];

	return (
		<DrawerWrapper>
			<DrawerHeader
				title={data?.getContentMetric?.metric?.name}
				menu={
					<CustomPopover
						layoutIcon={<MoreHoriz />}
						options={[
							{
								icon: <EditOutlined sx={{ fontSize: 18, fill: (theme) => theme.palette.grey[500] }} />,
								caption: <FormattedMessage id={'metric.drawer.edit-metric'} />,
								onAction: handleEditMetric,
							},
							{
								icon: <DeleteOutline sx={{ fontSize: 18, fill: (theme) => theme.palette.error.main }} />,
								caption: (
									<Typography color={(theme) => theme.palette.error.main}>
										<FormattedMessage id={'metric.drawer.remove'} />
									</Typography>
								),
								onAction: handleRemoveMetric,
							},
						]}
					/>
				}
			/>
			<Stack width={'100%'} direction={'row'} justifyContent={'flex-end'} alignItems={'center'}>
				<Button startIcon={<Add />} variant={'contained'} onClick={handleAddData}>
					<FormattedMessage id={'metrics-widget.history.add-data'} />
				</Button>
			</Stack>
			<Stack width={'100%'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
				<Switcher variant={'tabs'} config={tabsConfig} />
			</Stack>
			{tabState === 'HISTORY' && (
				<Stack width={'100%'} gap={3} mt={2}>
					<DataPointTable contentMetricId={contentMetricId} />
				</Stack>
			)}
			{tabState === 'ALERTS' && (
				<Stack width={'100%'} gap={3} mt={2}>
					<AlertsDrawerTab contentMetricId={contentMetricId} />
				</Stack>
			)}
			{tabState === 'SOURCE' && (
				<Stack width={'100%'} gap={3} mt={2}>
					<SourceDrawerTab contentMetricId={contentMetricId} />
				</Stack>
			)}
		</DrawerWrapper>
	);
};
